import { collection, deleteDoc, doc, setDoc } from "firebase/firestore";
import { RevisionAnnotation } from "~/types/RevisionAnnotation";

export const useSaveAnnotation = (documentId: string) => {
  const { save, saved } = useDocumentSavingStatusStore(documentId);

  const documentRevisionParagraph =
    useDocumentRevisionParagraphsStore(documentId);

  const documentHistoryStore = useDocumentHistoryStore(documentId);

  const saveAnnotation = async (
    annotation: RevisionAnnotation,
    hasLongComment: boolean = false,
    ignoreNextStateChange: boolean = false
  ) => {
    const { submittedDocument, revision } =
      useSubmittedDocumentStore(documentId);

    if (!documentId || !revision || !submittedDocument) {
      return;
    }

    const { selectedTokenPositions } = useDocumentSelectionStore(documentId);

    if (
      annotation.id == undefined &&
      selectedTokenPositions.length == 0 &&
      annotation.paragraphId == undefined
    ) {
      saved();
      return;
    }

    let tokens = annotation.tokenGlobalPositions;

    if (annotation.id == undefined) {
      tokens = [...selectedTokenPositions];
    }

    if (annotation.isSmartAnnotation == true) {
      // OK I need to first check the length
      // I think in either case though I set the paragraph id and the selected tokens would be blank.
      if (tokens.length > 0) {
        const tokenGlobalPosition = tokens[tokens.length - 1];
        const tokenStore = useParagraphTokenStore(
          documentId,
          tokenGlobalPosition
        );
        const paragraphId = tokenStore.paragraphId;

        annotation.paragraphId = paragraphId;
      }

      // We won't ever attach the smart annotations to tokens
      tokens = [];

      if (hasLongComment == true) {
        annotation.isEndLine = false;
      } else {
        annotation.isEndLine = true;
      }
    }

    save();

    const db = useFirestore();

    if (annotation.id == undefined) {
      // We only want to assign the selected tokens if we're adding a new annotation.

      tokens.sort();

      annotation.tokenGlobalPositions = tokens;

      annotation.startId = tokens[0] ?? null;

      const annotationCollectionRef = collection(
        db,
        `/documents/${documentId}/revisions/${revision.id}/annotations`
      );
      annotation.id = doc(annotationCollectionRef).id;
    }

    annotation.tokenText =
      documentRevisionParagraph.convertTokenGlobalPositionsToText(tokens);

    var annotationData = {
      ...annotation,
      lastUpdatedTimestamp: new Date().getTime(),
      type: annotation.type,
      documentId: documentId,
      revisionId: revision.id,
      classroomId: submittedDocument.classroomId,
      teacherId: submittedDocument.userId,
      studentId: submittedDocument.studentId,
      studentName: submittedDocument.studentName,
      assignmentId: submittedDocument.assignmentId,
      assignmentName: submittedDocument.assignmentName,
    };

    var allAnnotations = useDocumentAnnotationsStore(documentId).allAnnotations;

    // We need to replace the annotation in the all annotations array with the new one or add it if it doesn't exist
    var index = allAnnotations.findIndex((a) => a.id == annotation.id);

    if (index < 0) {
      allAnnotations.push(annotation);
    } else {
      allAnnotations[index] = annotation;
    }

    documentHistoryStore.saveAnnotationsState([annotation], allAnnotations);

    const annotationRef = doc(
      db,
      `/documents/${documentId}/revisions/${revision.id}/annotations/${annotation.id}`
    );

    await setDoc(annotationRef, annotationData);

    saved();
  };

  const deleteAnnotation = async (annotation: RevisionAnnotation) => {
    if (!annotation.id) {
      return;
    }

    const { revision } = useSubmittedDocumentStore(documentId);

    if (!documentId || !revision) {
      return;
    }

    save();
    const db = useFirestore();

    const annotationRef = doc(
      db,
      `/documents/${documentId}/revisions/${revision.id}/annotations/${annotation.id}`
    );

    useDocumentHistoryStore(documentId).saveAnnotationsState(
      [],
      useDocumentAnnotationsStore(documentId).allAnnotations
    );

    await deleteDoc(annotationRef);
    saved();
  };

  return { saveAnnotation, deleteAnnotation };
};
